/**
 * Hook to detect if text content exceeds a specified number of lines
 * Returns a ref to attach to the text element and a boolean indicating if overflow exists
 * @param maxLines - Maximum number of lines before showing "Show More" (default: 3)
 * @param content - Optional content dependency to re-check when content changes
 */

import { useEffect, useRef, useState } from 'react';

export const useTextOverflow = <T extends HTMLElement = HTMLElement>(
  maxLines: number = 3,
  content?: string
) => {
  const textRef = useRef<T>(null);
  const [exceedsLines, setExceedsLines] = useState(false);

  useEffect(() => {
    const checkOverflow = () => {
      if (!textRef.current) return;

      const element = textRef.current;

      // Temporarily remove line clamp to measure natural height
      const originalStyle = {
        display: element.style.display,
        WebkitLineClamp: element.style.webkitLineClamp,
        WebkitBoxOrient: element.style.webkitBoxOrient,
        overflow: element.style.overflow,
      };

      // Create a clone to measure without affecting the original
      const clone = element.cloneNode(true) as HTMLElement;
      clone.style.visibility = 'hidden';
      clone.style.position = 'absolute';
      clone.style.width = `${element.offsetWidth}px`;
      clone.style.top = '-9999px';
      clone.style.display = originalStyle.display || 'block';
      clone.style.webkitLineClamp = 'none';
      clone.style.webkitBoxOrient = 'vertical';
      clone.style.overflow = 'visible';
      clone.style.height = 'auto';

      document.body.appendChild(clone);

      const naturalHeight = clone.scrollHeight;
      document.body.removeChild(clone);

      // Get computed styles to calculate line height
      const computedStyle = window.getComputedStyle(element);
      const lineHeight =
        parseFloat(computedStyle.lineHeight) ||
        parseFloat(computedStyle.fontSize) * 1.5;
      const expectedHeight = lineHeight * maxLines;

      // Check if natural height exceeds expected height (with small buffer)
      const hasOverflow = naturalHeight > expectedHeight + 2;

      setExceedsLines(hasOverflow);
    };

    // Check after a delay to ensure layout is complete
    const timeoutId = setTimeout(checkOverflow, 100);
    window.addEventListener('resize', checkOverflow);

    return () => {
      clearTimeout(timeoutId);
      window.removeEventListener('resize', checkOverflow);
    };
  }, [maxLines, content]);

  return { textRef, exceedsLines };
};
